import AbstractFormulaVisitor from "./AbstractFormulaVisitor.js";
import AdditionFormulaToken from "../../formulaToken/AdditionFormulaToken.js";
import ClosingBalanceTokenPeriodFilter from "../../formulaToken/periodFilters/ClosingBalanceTokenPeriodFilter.js";
import ClosingParenthesisFormulaToken from "../../formulaToken/ClosingParenthesisFormulaToken.js";
import ConstantFormulaToken from "../../formulaToken/ConstantFormulaToken.js";
import DivisionFormulaToken from "../../formulaToken/DivisionFormulaToken.js";
import FormulaTokenizationError from "./FormulaTokenizationError.js";
import KeyFigureFormulaToken from "../../formulaToken/KeyFigureFormulaToken.js";
import MultiplicationFormulaToken from "../../formulaToken/MultiplicationFormulaToken.js";
import OpeningBalanceTokenPeriodFilter from "../../formulaToken/periodFilters/OpeningBalanceTokenPeriodFilter.js";
import OpeningParenthesisFormulaToken from "../../formulaToken/OpeningParenthesisFormulaToken.js";
import SpecificationTokenizationError from "./SpecificationTokenizationError.js";
import SpecificationTokenizer from "./SpecificationTokenizer.js";
import SubtractionFormulaToken from "../../formulaToken/SubtractionFormulaToken.js";

/**
 * Visitor that takes a tree of formula nodes as input, and produces an array of
 * instances of subclasses of `AbstractFormulaToken`.
 */
export default class FormulaTokenizer extends AbstractFormulaVisitor {
	tokenize(node) {
		return this.visit(node);
	}

	visitAbstractNode(node) {
		throw new FormulaTokenizationError(node);
	}

	visitNaNode() {
		return [];
	}

	visitConstantNode(node) {
		return [
			new ConstantFormulaToken({
				value: node.getValue(),
			}),
		];
	}

	visitNegatedNode(node) {
		const result = this._assertGenerateOneToken(node);

		result.setCreditPositive(true);

		return [result];
	}

	visitSumNode(node) {
		return node
			.getOperands()
			.flatMap((operand) => [
				...this._visitAndWrapInParenthesesIfNeeded({
					node: operand,
					parent: node,
				}),
				new AdditionFormulaToken(),
			])
			.slice(0, -1);
	}

	visitAdditionNode(node) {
		return this._tokenizeBinaryOperator({
			node,
			TokenClass: AdditionFormulaToken,
		});
	}

	visitDivisionNode(node) {
		return this._tokenizeBinaryOperator({
			node,
			TokenClass: DivisionFormulaToken,
		});
	}

	visitMultiplicationNode(node) {
		return this._tokenizeBinaryOperator({
			node,
			TokenClass: MultiplicationFormulaToken,
		});
	}

	visitSubtractionNode(node) {
		return this._tokenizeBinaryOperator({
			node,
			TokenClass: SubtractionFormulaToken,
		});
	}

	visitAccountNode(node) {
		const tokenizer = new SpecificationTokenizer();

		try {
			const specificationTokens = tokenizer.visit(
				node.getAccountSpecification(),
			);
			return [specificationTokens];
		} catch (error) {
			if (!(error instanceof SpecificationTokenizationError)) {
				throw error;
			}

			throw new FormulaTokenizationError(node);
		}
	}

	visitDimensionNode(node) {
		const result = this._assertGenerateOneToken(node);

		result.setDimensionSpecification(node.getDimensionSpecification());

		return [result];
	}

	visitPeriodShiftFormulaNode(node) {
		const result = this._assertGenerateOneToken(node);

		result.setShift(node.getShift());
		result.setShiftPeriodType(node.getShiftPeriodType());

		return [result];
	}

	visitPeriodChangeFormulaNode(node) {
		const result = this._assertGenerateOneToken(node);

		result.setPeriodType(node.getPeriodType());

		return [result];
	}

	visitPeriodRollingFormulaNode(node) {
		const rollingLength = node.getRollingLength();
		const rollingPeriodType = node.getPeriodType();
		const isRolling = !!(rollingLength && rollingPeriodType);

		const result = this._assertGenerateOneToken(node);

		result.setRollingPeriodType(rollingPeriodType);
		result.setRollingLength(rollingLength);
		result.setIsRolling(isRolling);

		return [result];
	}

	visitOpeningBalanceNode(node) {
		const result = this._assertGenerateOneToken(node);

		result.setPeriodFilter(OpeningBalanceTokenPeriodFilter.instance());

		return [result];
	}

	visitClosingBalanceNode(node) {
		const result = this._assertGenerateOneToken(node);

		result.setPeriodFilter(ClosingBalanceTokenPeriodFilter.instance());

		return [result];
	}

	visitKeyFigureNode(node) {
		return [
			new KeyFigureFormulaToken({
				keyFigure: node.getKeyFigure(),
			}),
		];
	}

	_visitAndWrapInParenthesesIfNeeded({ node, parent }) {
		const result = this.visit(node);
		if (node.precedence() < parent.precedence()) {
			return [
				new OpeningParenthesisFormulaToken(),
				...result,
				new ClosingParenthesisFormulaToken(),
			];
		}
		return result;
	}

	_tokenizeBinaryOperator({ node, TokenClass }) {
		return [
			...this._visitAndWrapInParenthesesIfNeeded({
				node: node.getLeftOperand(),
				parent: node,
			}),
			new TokenClass(),
			...this._visitAndWrapInParenthesesIfNeeded({
				node: node.getRightOperand(),
				parent: node,
			}),
		];
	}

	_assertGenerateOneToken(wrapperNode) {
		const tokens = this.visit(wrapperNode.getExpression());

		if (tokens.length > 1) {
			throw new FormulaTokenizationError(wrapperNode);
		}

		return tokens[0];
	}
}
